import { useEffect, useRef, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { LogOut, Settings, ChevronDown, Briefcase } from 'lucide-react'
import { Avatar } from '@/components/common/Avatar'
import { useAuthStore } from '@/stores/useAuthStore'
import { logout as logoutApi } from '@/api/services/auth.service'
import { queryClient } from '@/main'
import { cn } from '@/lib/cn'
import { useMe } from '@/features/settings/hooks/useMe'
import { useMyCompanies } from '@/features/companies/hooks/useCompanies'
import type { MeData } from '@/types/api.types'
import { Badge } from '@/components/ui/Badge'

function getRoleNameFromMe(me: MeData | undefined): string | undefined {
  const roleId = me?.roleId
  if (!roleId || typeof roleId === 'string') return undefined
  const raw = roleId.name?.trim()
  return raw || undefined
}

export function UserMenu(): React.ReactElement {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const clearAuth = useAuthStore((s) => s.logout)
  const { data: me } = useMe()
  const { data: myCompanies = [], isLoading: companiesLoading } = useMyCompanies()

  const [isOpen, setIsOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const meName = [me?.firstName, me?.lastName].filter(Boolean).join(' ').trim()
  const displayName = meName || user?.name || user?.email || 'User'
  const roleName = getRoleNameFromMe(me)

  const handleLogout = (): void => {
    setIsOpen(false)
    logoutApi().finally(() => {
      queryClient.clear()
      clearAuth()
      navigate('/login', { replace: true })
    })
  }

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setIsOpen(false)
    }
    const handleClickOutside = (e: MouseEvent): void => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex items-center gap-2 rounded-xl px-2 py-1.5 text-sm transition-colors hover:bg-gray-100/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 dark:hover:bg-white/5"
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="User menu"
      >
        <Avatar
          name={displayName}
          src={me?.avatar ?? user?.avatar ?? undefined}
          size="md"
          className="rounded-xl"
        />
        <span className="hidden max-w-32 truncate text-gray-700 dark:text-gray-300 sm:block">
          {displayName}
        </span>
        <ChevronDown
          className={cn(
            'hidden h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform duration-150 sm:block',
            isOpen && 'rotate-180'
          )}
          aria-hidden
        />
      </button>

      {isOpen && (
        <div
          className="absolute right-0 top-full z-50 mt-2 w-56 rounded-2xl border border-gray-200/60 bg-white/90 py-1 shadow-xl shadow-gray-200/40 backdrop-blur-xl dark:border-white/10 dark:bg-gray-900/90 dark:shadow-black/20"
          role="menu"
          aria-label="User options"
        >
          <div className="space-y-1.5 border-b border-gray-100 px-4 py-3 dark:border-gray-700">
            <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
              {displayName}
            </p>
            {roleName && <Badge variant="info">{roleName}</Badge>}
            {user?.email && user.name !== user.email && (
              <p className="truncate text-xs text-gray-500 dark:text-gray-400">{user.email}</p>
            )}
          </div>

          <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-700">
            <div className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <Briefcase className="h-3.5 w-3.5 shrink-0" aria-hidden />
              Companies
            </div>
            {companiesLoading ? (
              <p className="text-xs text-gray-500 dark:text-gray-400">Loading companies...</p>
            ) : myCompanies.length > 0 ? (
              <div className="space-y-1">
                {myCompanies.map((company) => (
                  <Link
                    key={company._id}
                    to={`/companies/${company._id}`}
                    role="menuitem"
                    onClick={() => setIsOpen(false)}
                    className="block truncate rounded-lg px-2 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700/50"
                    title={company.name}
                  >
                    {company.name}
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-500 dark:text-gray-400">No company memberships</p>
            )}
          </div>

          <div className="py-1">
            <Link
              to="/settings"
              role="menuitem"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700/50"
            >
              <Settings className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
              Settings
            </Link>
          </div>

          <div className="border-t border-gray-100 py-1 dark:border-gray-700">
            <button
              type="button"
              role="menuitem"
              onClick={handleLogout}
              className="flex w-full items-center gap-2.5 px-4 py-2 text-sm text-danger transition-colors hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <LogOut className="h-4 w-4 shrink-0" aria-hidden />
              Sign out
            </button>
          </div>

          {import.meta.env.VITE_APP_VERSION ? (
            <p className="border-t border-gray-100 px-4 py-2 text-[11px] tabular-nums text-gray-400 dark:border-gray-700 dark:text-gray-500">
              Version {import.meta.env.VITE_APP_VERSION}
            </p>
          ) : null}
        </div>
      )}
    </div>
  )
}

UserMenu.displayName = 'UserMenu'
